import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../../api/header";
import apiClient from "../../../api/apiClient";
import { toast } from "sonner";
import { all_routes } from "../../../Router/all_routes";

// UI Components
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ReactSelect } from "@/components/ui/react-select";
import { Switch } from "@/components/ui/switch";
import DefaultEditor from "react-simple-wysiwyg";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Icons
import { GripVertical, Plus, Trash2, Save, ArrowLeft } from "lucide-react";

// Dnd Kit
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Generate a random ID for new items
const generateId = () => Math.random().toString(36).substr(2, 9);

const SortableItemRow = ({
  item,
  index,
  orderItemChange,
  removeItem,
  products,
  setProductSearch,
  hasNextProductsPage,
  fetchNextProductsPage,
  productsLoading,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.dnd_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 p-4 bg-card border rounded-lg mb-3 shadow-sm relative z-0"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none flex-shrink-0"
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div className="flex-grow grid grid-cols-12 gap-4 items-center">
        {/* Product Select */}
        <div
          className="col-span-12 md:col-span-6"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Label className="md:hidden mb-2 block">Product</Label>
          <ReactSelect
            isLoading={productsLoading}
            onInputChange={(val) => setProductSearch(val)}
            onMenuScrollToBottom={() => {
              if (hasNextProductsPage) fetchNextProductsPage();
            }}
            filterOption={() => true}
            value={(() => {
              if (!item.product_id) return null;
              const p =
                item.product || products.find((p) => p.id === item.product_id);
              const label = p
                ? `${p.name}${p.sku ? ` (SKU: ${p.sku})` : ""}`
                : "Select Product";
              return { value: item.product_id, label };
            })()}
            onChange={(selectedOption) => {
              const selectedProduct = products.find(
                (p) => p.id === selectedOption?.value,
              );
              orderItemChange(
                index,
                "product_id",
                selectedOption ? selectedOption.value : "",
              );
              orderItemChange(index, "product", selectedProduct || null);
            }}
            options={products.map((product) => ({
              value: product.id,
              label: `${product.name}${product.sku ? ` (SKU: ${product.sku})` : ""}`,
            }))}
            placeholder="Select Product..."
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>

        {/* Quantity */}
        <div
          className="col-span-6 md:col-span-2"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Label className="md:hidden mb-2 block">Qty</Label>
          <Input
            type="number"
            min="1"
            value={item.quantity}
            onChange={(e) => orderItemChange(index, "quantity", e.target.value)}
          />
        </div>

        {/* Is Optional */}
        <div
          className="col-span-4 md:col-span-3 flex items-center gap-2"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Switch
            id={`optional-${item.dnd_id}`}
            checked={item.is_optional}
            onCheckedChange={(checked) =>
              orderItemChange(index, "is_optional", checked)
            }
          />
          <Label
            htmlFor={`optional-${item.dnd_id}`}
            className="cursor-pointer mb-0"
          >
            Optional?
          </Label>
        </div>

        {/* Delete Action */}
        <div
          className="col-span-2 md:col-span-1 text-right flex justify-end"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive hover:bg-destructive/10"
            onClick={() => removeItem(index)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddAssembleConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    show_component_prices: false,
  });

  const { data: configData, isLoading: isConfigLoading } = useQuery({
    queryKey: ["assemble-config", id],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.ASSEMBLE.CONFIG_DETAILS(id),
      );
      return res.data?.data || res.data;
    },
    enabled: !!id,
  });

  const [items, setItems] = useState([
    {
      dnd_id: generateId(),
      product_id: "",
      product: null,
      quantity: 1,
      is_optional: false,
    },
  ]);

  useEffect(() => {
    if (configData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData({
        name: configData.name || "",
        description: configData.description || "",
        show_component_prices: !!configData.show_component_prices,
      });
      if (configData.items && configData.items.length > 0) {
        setItems(
          configData.items.map((item) => ({
            dnd_id: generateId(),
            product_id: item.product_id || "",
            product: item.product || null,
            quantity: item.quantity || 1,
            is_optional: !!item.is_optional,
          })),
        );
      }
    }
  }, [configData]);

  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearch]);

  // Infinite query for products
  const {
    data: productsData,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
    isFetchingNextPage: isFetchingNextProductsPage,
    isLoading: isProductsLoading,
  } = useInfiniteQuery({
    queryKey: ["assemble-products", debouncedSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(
        API_ENDPOINTS.INVENTORY.POS_PRODUCTS,
        {
          params: {
            perPage: 15,
            page: pageParam,
            search: debouncedSearch,
            store_id,
          },
        },
      );
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5,
  });

  const products = useMemo(() => {
    if (!productsData) return [];
    return productsData.pages.flatMap((page) => page.data || []);
  }, [productsData]);

  const productsLoading = isProductsLoading || isFetchingNextProductsPage;

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.dnd_id === active.id);
        const newIndex = items.findIndex((i) => i.dnd_id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const orderItemChange = (index, key, value) => {
    const newItems = [...items];
    newItems[index][key] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        dnd_id: generateId(),
        product_id: "",
        product: null,
        quantity: 1,
        is_optional: false,
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const createConfigMutation = useMutation({
    mutationFn: async (payload) => {
      if (id) {
        return await apiClient.put(
          API_ENDPOINTS.ASSEMBLE.CONFIG_DETAILS(id),
          payload,
        );
      }
      return await apiClient.post(API_ENDPOINTS.ASSEMBLE.CONFIGS, payload);
    },
    onSuccess: () => {
      toast.success(
        `Assemble config ${id ? "updated" : "created"} successfully`,
      );
      queryClient.invalidateQueries({ queryKey: ["assemble-configs"] });
      navigate(all_routes.assembleConfigs);
      setFormData({
        name: "",
        description: "",
        show_component_prices: false,
      });
      setItems([
        {
          dnd_id: generateId(),
          product_id: "",
          product: null,
          quantity: 1,
          is_optional: false,
        },
      ]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create assemble config",
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Config name is required");
      return;
    }

    if (!store_id) {
      toast.error("Store ID is required");
      return;
    }

    if (items.length === 0) {
      toast.error("Please add at least one component");
      return;
    }

    const invalidItem = items.find(
      (i) => !i.product_id || parseInt(i.quantity, 10) < 1,
    );
    if (invalidItem) {
      toast.error(
        "Please ensure all components have a product selected and valid quantity",
      );
      return;
    }

    // Payload mapping
    const payload = {
      store_id,
      name: formData.name,
      description: formData.description || null,
      show_component_prices: formData.show_component_prices,
      items: items.map((item, idx) => ({
        product_id: item.product_id,
        quantity: parseInt(item.quantity, 10),
        is_optional: item.is_optional,
        sort_order: idx, // The visual order determines sort_order
      })),
    };

    createConfigMutation.mutate(payload);
  };

  if (isConfigLoading) {
    return <div className="p-8 text-center">Loading config details...</div>;
  }

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeaderHeading
            title={id ? "Edit Assemble Config" : "Add Assemble Config"}
            subtitle={
              id
                ? "Update an existing PC configuration"
                : "Create a new PC assembly configuration"
            }
          />
        </div>
      </PageHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Configuration Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g. Budget Gaming PC Build"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                maxLength={255}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <DefaultEditor
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                containerProps={{ style: { minHeight: "250px" } }}
              />
            </div>

            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center space-x-2">
                <Switch
                  id="show_prices"
                  checked={formData.show_component_prices}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, show_component_prices: checked })
                  }
                />
                <Label htmlFor="show_prices" className="cursor-pointer">
                  Show Component Prices
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Components</CardTitle>
            <Button
              type="button"
              onClick={addItem}
              size="sm"
              variant="secondary"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Component
            </Button>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/20">
                No components added. Click "Add Component" to start building.
              </div>
            ) : (
              <div className="space-y-1">
                {/* Header row for md screens */}
                <div className="hidden md:flex items-center gap-4 px-4 pb-2 text-sm font-medium text-muted-foreground ml-9">
                  <div className="w-[49%]">Product</div>
                  <div className="w-[15%]">Quantity</div>
                  <div className="w-[20%]">Status</div>
                  <div className="flex-1 text-right">Actions</div>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={items.map((i) => i.dnd_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div>
                      {items.map((item, index) => (
                        <SortableItemRow
                          key={item.dnd_id}
                          item={item}
                          index={index}
                          orderItemChange={orderItemChange}
                          removeItem={removeItem}
                          products={products}
                          setProductSearch={setProductSearch}
                          productsLoading={productsLoading}
                          hasNextProductsPage={hasNextProductsPage}
                          fetchNextProductsPage={fetchNextProductsPage}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end gap-3 pt-6 border-t mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createConfigMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {createConfigMutation.isPending
                ? "Saving..."
                : "Save Configuration"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
};

export default AddAssembleConfig;
